import { useEffect, useRef, useState } from 'react';

import { ExportStatus } from '../../../../../../backend/src/types/types';
import { useExportEvents, BaseExportEvent } from '@/api/hooks/useExportEvents';
import { useGetExportStatus } from '@/api/queries/useGetExportStatus';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/new/components/shadcn/dialog';

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

interface Props {
  /** Callback to trigger the download when complete */
  onDownload: () => void;
  /** Error message if creation failed */
  error?: string;
  /** Whether the export already exists and is ready */
  exportReady: boolean;
  /** Name of the item being exported (for display) */
  itemName: string;
  /** Optional: Additional description for the ready state */
  readyDescription?: string;
  /** Archive creation progress percentage (0-100) */
  archiveProgress?: number;
  /** Optional: Custom text for download button (defaults to "Download") */
  downloadButtonText?: string;
  /** Optional: Callback to retry on error (shows "Try Again" button) */
  onRetry?: () => void;
  /** Upload progress percentage (0-100) when phase is 'uploading' */
  uploadProgress?: number;
  totalBytes?: number;
  processedBytes?: number;
}

export function ExportProgressContent({
  onDownload,
  error,
  exportReady,
  itemName,
  readyDescription,
  archiveProgress = 0,
  downloadButtonText = 'Download',
  onRetry,
  uploadProgress = 0,
  totalBytes = 0,
  processedBytes = 0,
}: Props) {
  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center gap-2">
        <div className="flex items-center gap-3 rounded-md border border-red-200 bg-red-50/80 px-3 py-2 text-red-600">
          <Icon className="lucide--alert-triangle text-red-500" />
          Error creating export
        </div>

        <p className="mt-6">Sorry, something went wrong trying to create your export.</p>
        <div className="text-center text-gray-600">{error}</div>

        {onRetry && (
          <Button onClick={onRetry} className="w-full">
            Try Again
          </Button>
        )}
      </div>
    );
  }

  // Ready state
  if (exportReady) {
    return (
      <div className="flex flex-col items-center justify-center gap-4">
        <div className="mt-4 flex items-center gap-3 text-base font-medium">
          <Icon className="lucide--check-circle text-teal-500" />
          <div className="text-gray-900">Ready for download</div>
        </div>

        <div className="mt-3 text-center text-balance text-gray-600">
          {readyDescription || `${itemName} with source documents has been packaged.`}
        </div>
        <div className="mt-4 flex w-full gap-2">
          <Button variant="primary" onClick={onDownload} className="w-full">
            {downloadButtonText}
          </Button>
        </div>
      </div>
    );
  }

  // Single continuous progress: archive (source bytes read) and upload (dest
  // bytes written) overlap under the hood, so we surface the higher of the two
  // and avoid showing a separate "finalising" phase. High-water mark keeps the
  // bar monotonic across the archive→upload handoff.
  const isInitialising = archiveProgress === 0 && uploadProgress === 0;
  const highWaterRef = useRef(0);
  if (isInitialising) {
    highWaterRef.current = 0;
  } else {
    highWaterRef.current = Math.max(highWaterRef.current, archiveProgress, uploadProgress);
  }
  const displayProgress = highWaterRef.current;

  return (
    <div className="flex flex-col items-center justify-center gap-4">
      <div className="my-4 w-full px-3">
        <div className="mb-2.5 flex items-center justify-between text-sm text-gray-500">
          <span className="animate-pulse">Preparing your download...</span>
          {!isInitialising && (
            <span>
              {totalBytes > 0 && `${formatBytes(processedBytes)} / ${formatBytes(totalBytes)} · `}
              {Math.round(displayProgress)}%
            </span>
          )}
        </div>

        <div className="h-2.5 w-full overflow-hidden rounded-full bg-gray-200">
          <div
            className="h-2.5 rounded-full bg-teal-400 transition-all duration-300"
            style={{ width: `${displayProgress}%` }}
          />
        </div>
      </div>

      <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
        <Icon className="lucide--mail mt-px" />
        You will receive an email when the download is ready.
      </div>
    </div>
  );
}

/**
 * Configuration for export endpoints
 */
export interface ExportConfig {
  /** Base endpoint for progress SSE */
  progressEndpoint: string;
  /**
   * Terminal-status endpoint URL polled alongside SSE. Gated by the same
   * per-resource download permission as the SSE sibling.
   */
  statusEndpoint?: string;
  /** Export ID appended as query param to filter SSE events */
  exportId: string | null;
}

/**
 * Props for GenericExportProgressModal
 */
interface GenericExportProgressModalProps<TEvent extends BaseExportEvent> {
  // Modal control
  isOpen: boolean;
  onClose: () => void;

  // Export configuration
  exportConfig: ExportConfig;

  // Display & behavior
  itemName: string;
  onDownload: () => Promise<void>;
  isCreating: boolean;
  error?: string;

  // Optional customization
  readyDescription?: string;
  downloadButtonText?: string;
  onRetry?: () => void;
}

export const GenericExportProgressModal = <TEvent extends BaseExportEvent>({
  isOpen,
  onClose,
  exportConfig,
  itemName,
  onDownload,
  isCreating,
  error,
  readyDescription,
  downloadButtonText,
  onRetry,
}: GenericExportProgressModalProps<TEvent>) => {
  const [exportReady, setExportReady] = useState(false);
  const [archiveProgress, setArchiveProgress] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [totalBytes, setTotalBytes] = useState(0);
  const [processedBytes, setProcessedBytes] = useState(0);
  const [sseError, setSseError] = useState<string>();

  // Reset state when a new export starts
  useEffect(() => {
    if (isCreating) {
      setExportReady(false);
      setArchiveProgress(0);
      setUploadProgress(0);
      setTotalBytes(0);
      setProcessedBytes(0);
      setSseError(undefined);
    }
  }, [isCreating]);

  useExportEvents<TEvent>({
    progressEndpoint: `${exportConfig.progressEndpoint}?exportId=${exportConfig.exportId}`,
    onEvent: (event) => {
      if (event.type.includes('progress')) {
        setArchiveProgress(event.archiveProgress || 0);
        if (event.totalBytes !== undefined) setTotalBytes(event.totalBytes);
        if (event.processedBytes !== undefined) setProcessedBytes(event.processedBytes);
        if (event.uploadProgress !== undefined) setUploadProgress(event.uploadProgress);
      } else if (event.type.includes('complete')) {
        setArchiveProgress(100);
        setExportReady(true);
      } else if (event.type.includes('error')) {
        setSseError(event.error || 'An unexpected error occurred while creating the export.');
      }
    },
    enabled: !!exportConfig.exportId && !exportReady && !sseError,
  });

  // Poll unconditionally alongside SSE — SSE can fail silently (proxy buffers
  // the stream without erroring), so polling is the source of truth for the
  // terminal state.
  const { data: polledStatus } = useGetExportStatus({
    statusEndpoint: exportConfig.statusEndpoint,
    exportId: exportConfig.exportId,
    enabled: !!exportConfig.exportId && !exportReady && !sseError,
  });

  useEffect(() => {
    if (!polledStatus) return;
    if (polledStatus.status === ExportStatus.Completed) {
      setArchiveProgress(100);
      setExportReady(true);
    } else if (polledStatus.status === ExportStatus.Failed) {
      setSseError(polledStatus.error || 'An unexpected error occurred while creating the export.');
    }
  }, [polledStatus]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-120 focus:outline-none">
        <DialogHeader>
          <DialogTitle>
            <Icon className="lucide--download" /> Download
          </DialogTitle>
          <DialogDescription>Please wait while your export is in progress</DialogDescription>
        </DialogHeader>

        <ExportProgressContent
          onDownload={onDownload}
          error={error || sseError}
          exportReady={exportReady}
          itemName={itemName}
          readyDescription={readyDescription}
          archiveProgress={archiveProgress}
          downloadButtonText={downloadButtonText}
          onRetry={onRetry}
          uploadProgress={uploadProgress}
          totalBytes={totalBytes}
          processedBytes={processedBytes}
        />
      </DialogContent>
    </Dialog>
  );
};
